import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toastSuccessWithLink } from '~/utils/toastSuccessWithLink';
import { Button } from '~/components/ui';
import { WizardFooter, WizardModal } from '~/components/overlays';
import { useCreateBuyList, useGetBuyList } from '~/api/buyList';
import { useGetBuyerInventoryByProductAndLocation } from '~/api/inventory';
import { useGetCart } from '~/api';
import type {
  BuyListQuery,
  BuyerInventoryByProductAndLocationQuery,
  Quantity,
  UnitCode,
} from '~/graphql/graphql';
import { toastGraphQLError } from '~/utils';
import {
  getPlatformProductDimensions,
  roundUpToBundleQty,
} from '~/utils/order';
import {
  calculateTargetOrderSize,
  type CartQuantity,
} from '~/utils/targetOrderSize';
import { cartQuantityForProductLength } from '~/utils/cartQuantity';
import { toStandardUnits } from '~/utils/unitConversion';
import { usePreferredUnit } from '~/hooks/usePreferredUnit';
import {
  BuyListDetailLoading,
  BuyListItemsStepsFooter,
  BuyListStepContent,
  type BuyListItemsStep,
} from '~/pages/buy-lists/sections/BuyListItemsSteps';
import {
  isInitialPickComplete,
  type InitialProductLengthPick,
} from '~/components/common/productLengthChoice';
import {
  initialPickOf,
  productSummaryLine,
  type AddToBuyListLocation,
  type AddToBuyListProductLength,
} from './addToBuyListTypes';
import { AddToBuyListChooseStep } from './AddToBuyListChooseStep';
import {
  AddToBuyListCreateForm,
  type AddToBuyListDraft,
} from './AddToBuyListCreateForm';
import { useMyOpenBuyLists, type BuyListTarget } from './useMyOpenBuyLists';

type BuyerInventoryAtBuyListLocation =
  BuyerInventoryByProductAndLocationQuery['buyerInventoryByProductAndLocation'];

type AddRowPick =
  | { kind: 'awaiting-suggestion' }
  | { kind: 'suggested'; seed: InitialProductLengthPick }
  | { kind: 'pending' }
  | { kind: 'none' };

const AWAITING_SUGGESTION: AddRowPick = { kind: 'awaiting-suggestion' };
const PENDING_PICK: AddRowPick = { kind: 'pending' };
const NO_PENDING_PICK: AddRowPick = { kind: 'none' };

type AddToBuyListModalProps = {
  onClose: () => void;
  location: AddToBuyListLocation;
  platformProductsLength: AddToBuyListProductLength;
};

function suggestionInPreferredUnit(
  suggestedQuantity: Quantity | null | undefined,
  preferredUnit: UnitCode,
  platformProductsLength: AddToBuyListProductLength,
  piecesPerBundle: number
): Quantity | null {
  if (!suggestedQuantity || suggestedQuantity.value <= 0) return null;

  const { widthInches, thicknessInches, lengthInches } =
    getPlatformProductDimensions(
      platformProductsLength.platformProduct,
      platformProductsLength.length,
      platformProductsLength.isNominal
    );
  if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0) {
    return null;
  }

  const convertedValue = toStandardUnits(
    suggestedQuantity.value,
    suggestedQuantity.unit,
    preferredUnit,
    lengthInches,
    widthInches,
    thicknessInches
  );
  const bundledValue = roundUpToBundleQty(
    convertedValue,
    piecesPerBundle,
    preferredUnit,
    lengthInches,
    widthInches,
    thicknessInches
  );
  return bundledValue > 0 ? { value: bundledValue, unit: preferredUnit } : null;
}

function targetOrderSizeFor(
  inventory: BuyerInventoryAtBuyListLocation,
  platformProductsLength: AddToBuyListProductLength,
  cartQuantity: CartQuantity
): Quantity | null {
  if (!inventory) return null;

  const { widthInches, thicknessInches, lengthInches } =
    getPlatformProductDimensions(
      platformProductsLength.platformProduct,
      platformProductsLength.length,
      platformProductsLength.isNominal
    );
  const configuration = inventory.buyerInventoryConfiguration;

  const targetOrderSize = calculateTargetOrderSize({
    usageRatePerWeek: configuration?.usageRatePerWeek,
    targetWeeksCovered: configuration?.targetWeeksCovered,
    currentStock: inventory.quantity,
    onOrderStock: inventory.onOrderStock,
    inboundStock: inventory.inboundStock,
    cartQuantity,
    dims: { lengthInches, widthInches, thicknessInches },
  });
  if (targetOrderSize === null) return null;

  return { value: targetOrderSize, unit: inventory.quantity.unit };
}

function savedItemFor(
  buyList: BuyListTarget,
  platformProductsLengthId: string
) {
  return buyList.items.find(
    (item) => item.platformProductsLength.id === platformProductsLengthId
  );
}

function latestVersionOf(
  chosenBuyList: BuyListTarget | null,
  openBuyLists: BuyListTarget[]
) {
  if (!chosenBuyList) return null;
  return (
    openBuyLists.find((buyList) => buyList.id === chosenBuyList.id) ??
    chosenBuyList
  );
}

function hasItemsOrTallies(buyListDetail: BuyListQuery | undefined) {
  return (
    !!buyListDetail &&
    buyListDetail.buyList.items.length + buyListDetail.buyList.tallies.length >
      0
  );
}

function withListsNotYetReturnedByTheQuery(
  openBuyLists: BuyListTarget[],
  createdBuyLists: BuyListTarget[]
): BuyListTarget[] {
  return [
    ...openBuyLists,
    ...createdBuyLists.filter(
      (createdBuyList) =>
        !openBuyLists.some((buyList) => buyList.id === createdBuyList.id)
    ),
  ];
}

export function AddToBuyListModal({
  onClose,
  location,
  platformProductsLength,
}: Readonly<AddToBuyListModalProps>) {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const { standardUnitsCode, category } =
    platformProductsLength.platformProduct;
  const preferredUnit = usePreferredUnit(category, standardUnitsCode);
  const [chosenBuyList, setChosenBuyList] = useState<BuyListTarget | null>(
    null
  );
  const [itemsStep, setItemsStep] = useState<BuyListItemsStep>('products');
  const [addRowPick, setAddRowPick] = useState<AddRowPick>(AWAITING_SUGGESTION);
  const [isSavingItems, setIsSavingItems] = useState(false);
  const [isCreateFormRequested, setIsCreateFormRequested] = useState(false);
  const [createDraft, setCreateDraft] = useState<AddToBuyListDraft | null>(
    null
  );
  const [createdBuyLists, setCreatedBuyLists] = useState<BuyListTarget[]>([]);

  const {
    openBuyLists,
    totalOpenBuyLists,
    isLoadingFirstPage,
    isLoadingMoreBuyLists,
    canLoadMoreBuyLists,
    hasBuyListsFailed,
    areBuyListsReadable,
    loadMoreBuyLists,
    retryBuyLists,
  } = useMyOpenBuyLists();
  const { mutate: createBuyList, isPending: isCreating } = useCreateBuyList();
  const isSaving = isCreating || isSavingItems;

  const selectableBuyLists = useMemo(
    () => withListsNotYetReturnedByTheQuery(openBuyLists, createdBuyLists),
    [openBuyLists, createdBuyLists]
  );

  const isCreateFormOpen =
    isCreateFormRequested ||
    (areBuyListsReadable && selectableBuyLists.length === 0);

  const selectedBuyList = latestVersionOf(chosenBuyList, selectableBuyLists);
  const { data: buyListDetail } = useGetBuyList(
    selectedBuyList ? selectedBuyList.id : null
  );
  const savedItem = selectedBuyList
    ? savedItemFor(selectedBuyList, platformProductsLength.id)
    : undefined;
  const buyListLocationId = selectedBuyList
    ? selectedBuyList.location.id
    : undefined;

  const {
    data: inventoryAtBuyListLocation,
    isLoading: isLoadingInventoryAtBuyListLocation,
  } = useGetBuyerInventoryByProductAndLocation(
    platformProductsLength.length.id,
    platformProductsLength.platformProduct.id,
    buyListLocationId ?? ''
  );

  const { data: cartsAtBuyListLocation, isLoading: isLoadingCarts } =
    useGetCart(buyListLocationId, { enabled: !!selectedBuyList });

  const cartQuantityAtBuyListLocation = useMemo(
    () =>
      cartQuantityForProductLength(
        cartsAtBuyListLocation?.carts,
        platformProductsLength.length.id,
        platformProductsLength.platformProduct.id,
        getPlatformProductDimensions(
          platformProductsLength.platformProduct,
          platformProductsLength.length,
          platformProductsLength.isNominal
        )
      ),
    [cartsAtBuyListLocation, platformProductsLength]
  );

  const suggestedQuantity = useMemo(
    () =>
      suggestionInPreferredUnit(
        targetOrderSizeFor(
          inventoryAtBuyListLocation?.buyerInventoryByProductAndLocation,
          platformProductsLength,
          cartQuantityAtBuyListLocation
        ),
        preferredUnit,
        platformProductsLength,
        platformProductsLength.platformProduct.piecesPerBundle.value
      ),
    [
      inventoryAtBuyListLocation,
      cartQuantityAtBuyListLocation,
      preferredUnit,
      platformProductsLength,
    ]
  );

  const isLoadingSuggestion =
    isLoadingInventoryAtBuyListLocation || isLoadingCarts;
  const suggestedPick = initialPickOf(
    platformProductsLength,
    savedItem ? savedItem.quantity : suggestedQuantity,
    savedItem ? savedItem.effectivePiecesPerBundle : undefined
  );
  if (
    selectedBuyList &&
    addRowPick.kind === 'awaiting-suggestion' &&
    !isLoadingSuggestion
  ) {
    setAddRowPick({ kind: 'suggested', seed: suggestedPick });
  }
  const shownSeed = addRowPick.kind === 'suggested' ? addRowPick.seed : null;
  const hasPendingPick =
    addRowPick.kind === 'pending' ||
    (!!shownSeed && isInitialPickComplete(shownSeed));

  const selectBuyList = (buyList: BuyListTarget) => {
    setChosenBuyList(buyList);
    setItemsStep('products');
    setAddRowPick(AWAITING_SUGGESTION);
  };

  const handlePendingPickChange = (isPending: boolean) =>
    setAddRowPick(isPending ? PENDING_PICK : NO_PENDING_PICK);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleCreate = () => {
    if (!createDraft) return;
    const note = createDraft.note.trim();
    createBuyList(
      {
        locationId: createDraft.locationId,
        transportVehicleId: createDraft.transportVehicleId,
        expectedShipDate: createDraft.expectedShipDate,
        note: note === '' ? null : note,
      },
      {
        onSuccess: (result) => {
          setCreatedBuyLists((previous) => [...previous, result.createBuyList]);
          selectBuyList(result.createBuyList);
        },
        onError: toastGraphQLError,
      }
    );
  };

  const handleBackToLists = () => {
    setChosenBuyList(null);
    setIsCreateFormRequested(false);
    setCreateDraft(null);
  };

  const handleItemsStepChange = (step: BuyListItemsStep) => {
    setItemsStep(step);
    setAddRowPick(NO_PENDING_PICK);
  };

  const handleDone = (buyListId: string) => {
    onClose();
    if (!hasItemsOrTallies(buyListDetail)) return;
    toastSuccessWithLink({
      message: t('inventory.buy_list.done_success'),
      linkLabel: t('inventory.buy_list.view_list'),
      path: `/buy-lists/view/${buyListId}`,
      navigate,
      testId: 'add-to-buy-list-view-list',
    });
  };

  const handleCreateRequested = () => {
    setIsCreateFormRequested(true);
    setCreateDraft(null);
  };

  const handleDraftChange = useCallback(
    (draft: AddToBuyListDraft | null) => setCreateDraft(draft),
    []
  );

  const renderItemsStep = (buyListId: string) =>
    addRowPick.kind === 'awaiting-suggestion' ? (
      <BuyListDetailLoading />
    ) : (
      <BuyListStepContent
        key={buyListId}
        buyListId={buyListId}
        step={itemsStep}
        onSavingChange={setIsSavingItems}
        onPendingPickChange={handlePendingPickChange}
        initialPick={shownSeed ?? undefined}
      />
    );

  const renderChooseStep = () => {
    if (isCreateFormOpen) {
      return (
        <AddToBuyListCreateForm
          initialLocationId={location.id}
          onBack={selectableBuyLists.length > 0 ? handleBackToLists : undefined}
          onDraftChange={handleDraftChange}
        />
      );
    }

    return (
      <AddToBuyListChooseStep
        currentLocationId={location.id}
        openBuyLists={selectableBuyLists}
        totalOpenBuyLists={totalOpenBuyLists}
        isLoadingFirstPage={isLoadingFirstPage}
        isLoadingMoreBuyLists={isLoadingMoreBuyLists}
        canLoadMoreBuyLists={canLoadMoreBuyLists}
        hasBuyListsFailed={hasBuyListsFailed}
        areBuyListsReadable={areBuyListsReadable}
        onLoadMore={loadMoreBuyLists}
        onRetry={() => void retryBuyLists()}
        onCreateRequested={handleCreateRequested}
        onSelect={selectBuyList}
      />
    );
  };

  const wizardSteps = (
    [
      ['choose', 'inventory.buy_list.step_choose'],
      ['products', 'inventory.buy_list.create_step_products'],
      ['review', 'inventory.buy_list.create_step_review'],
    ] as const
  ).map(([id, labelKey]) => ({
    id,
    label: t(labelKey),
    title: t('inventory.buy_list.title'),
    description: productSummaryLine(platformProductsLength),
  }));

  const chooseStepFooter = (
    <WizardFooter
      start={
        <Button
          variant="secondary"
          width="auto"
          onClick={handleClose}
          data-testid="add-to-buy-list-cancel-button"
        >
          {t('inventory.cancel')}
        </Button>
      }
      end={
        isCreateFormOpen && (
          <Button
            variant="primary"
            width="auto"
            onClick={handleCreate}
            disabled={!createDraft || isCreating}
            data-testid="add-to-buy-list-create-submit"
          >
            {t('inventory.buy_list.create_submit')}
          </Button>
        )
      }
    />
  );

  return (
    <WizardModal
      testIdPrefix="add-to-buy-list"
      testId="add-to-buy-list-modal"
      onClose={onClose}
      isPending={isSaving}
      steps={wizardSteps}
      currentStepId={selectedBuyList ? itemsStep : 'choose'}
      stepIndicatorTestId="add-to-buy-list-steps"
      stepIndicatorAriaLabel={t('inventory.buy_list.steps_label')}
      footer={
        selectedBuyList ? (
          <BuyListItemsStepsFooter
            step={itemsStep}
            isSaving={isSaving}
            hasPendingPick={hasPendingPick}
            testIdPrefix="add-to-buy-list"
            onStepChange={handleItemsStepChange}
            onBackFromProducts={handleBackToLists}
            onDone={() => handleDone(selectedBuyList.id)}
          />
        ) : (
          chooseStepFooter
        )
      }
    >
      {selectedBuyList
        ? renderItemsStep(selectedBuyList.id)
        : renderChooseStep()}
    </WizardModal>
  );
}
